import type React from "react";
import { useContext, useRef, useState } from "react";
import { Button, ButtonGroup } from "rsuite";
import { LanguageContext } from "../../../app/context/LanguageContext";
import { useGenerateSpeakingFeedbackMutation } from "../../../entities/OpenAI/openAIapi";
import {
	useGetSpeakingFeedbackByUserQuery,
	useSaveSpeakingFeedbackMutation,
} from "../../../entities/SpeakingFeedback/speakingFeedbackApi";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { getUserFromPersistedAuth } from "../../../shared/helpers/getUserFromPersistedRoot";
import { isMobileDevice } from "../../../shared/helpers/isMobileDevice";
import { getLocaleByLanguage } from "../TranslateSentencesOrallyActivity/helpers";
import style from "./QaWithAI.module.scss";
import { FeedbackComponent } from "./components/Feedback/Feedback";

type Props = {
	data: {
		question: string;
		modelAnswer: string;
	}[];
	activityId: string;
};

interface SpeechRecognitionEvent {
	results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
	[index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
	[index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
	transcript: string;
}

export const QaWithAI: React.FC<Props> = ({ data, activityId }) => {
	const [revealModelAnswer, setRevealModelAnswer] = useState<{
		[key: number]: boolean;
	}>({});
	const [isListening, setIsListening] = useState(false);
	const [speechResult, setSpeechResult] = useState("");
	const [generateSpeakingFeedback, { isLoading }] =
		useGenerateSpeakingFeedbackMutation();
	const [saveSpeakingFeedback] = useSaveSpeakingFeedbackMutation();
	const user = getUserFromPersistedAuth();
	const { triggerActivityCompletion } = useActivityCompletion();

	const { data: attempts } = useGetSpeakingFeedbackByUserQuery({
		userId: user?.userId || "",
		speakingActivityId: activityId,
	});

	const { language } = useContext(LanguageContext);

	const recognitionRef = useRef<null>(null);

	const handleRevealModelAnswer = (index: number) => {
		setRevealModelAnswer((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	const speechRecognitionStart = () => {
		const SpeechRecognition =
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			window.SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (!SpeechRecognition) {
			alert("SpeechRecognition API is not supported in your browser.");
			return;
		}

		if (isListening && recognitionRef.current) {
			// @ts-expect-error SpeechRecognition type definition is missing
			recognitionRef?.current?.stop();
			setIsListening(false);
			return;
		}

		const recognition = new SpeechRecognition();
		recognition.lang = getLocaleByLanguage(language);
		recognition.interimResults = true;

		if (!isMobileDevice()) {
			recognition.continuous = true;
		} else {
			recognition.continuous = false;
		}

		recognition.onstart = () => {
			console.log("Speech recognition started");
			setIsListening(true);
		};

		recognition.onend = () => {
			console.log("Speech recognition ended");
			setIsListening(false);
		};

		recognition.onresult = (event: SpeechRecognitionEvent) => {
			const transcript = Array.from(event.results as [])
				.map((result: SpeechRecognitionResult) => result[0].transcript)
				.join("");
			setSpeechResult(transcript);
			console.log("Recognized Speech:", transcript);
		};
		// @ts-expect-error SpeechRecognition type definition is missing
		recognitionRef.current = recognition;
		recognition.start();
	};

	const submitAnswer = async () => {
		try {
			if ((attempts?.length ?? 0) >= 3) {
				alert("You have reached the maximum number of attempts");
				return;
			}

			if (!speechResult?.trim()) {
				alert("Please provide an answer before submitting.");
				return;
			}

			if (speechResult.trim().split(/\s+/).length > 100) {
				alert("Your answer should be less than 100 words");
				return;
			}

			const response = (await generateSpeakingFeedback({
				content: speechResult,
			})) as { data: { overallBandScore: number } };

			if (response?.data) {
				const answer = speechResult;
				const feedback = JSON.stringify(response.data);
				const score = response.data.overallBandScore;

				await saveSpeakingFeedback({
					userId: user?.userId || "",
					speakingActivityId: activityId,
					answer,
					feedback,
					score,
				}).unwrap();

				if (attempts?.length === 2) {
					await triggerActivityCompletion(activityId, 16, () =>
						setSpeechResult(""),
					);
				}
			} else {
				alert("Failed to generate feedback. Please try again.");
			}
		} catch (error) {
			console.error("Error submitting answer:", error);
			alert(
				"An error occurred while submitting your answer. Please try again.",
			);
		}
	};

	return (
		<div className={style.container}>
			<h2>Q&A with a Virtual Language Expert</h2>
			{data.map((item, index) => (
				<div key={item.question} className={style.qaItem}>
					<div className={style.question}>
						<strong>Question {index + 1}:</strong> {item.question}
					</div>
					<div className={style.actions}>
						<Button
							appearance="link"
							color="cyan"
							onClick={() => handleRevealModelAnswer(index)}
						>
							{revealModelAnswer[index] ? "Hide" : "Reveal"} Model Answer
						</Button>
					</div>
					{revealModelAnswer[index] && (
						<div className={style.modelAnswer}>
							<strong>Model Answer:</strong> {item.modelAnswer}
						</div>
					)}
				</div>
			))}
			<div className={style.qaItem}>
				<div className={style.question}>
					<strong>Try it yourself!</strong> {data[data.length - 1].question}
				</div>
				<div className={style.userInput}>
					<span>{speechResult?.trim()?.split(/\s+/)?.length || 0} words</span>
					<textarea
						value={speechResult}
						onChange={(e) => setSpeechResult(e.target.value)}
						placeholder="Write your answer here..."
						className={style.textArea}
						rows={10}
					/>
				</div>
				<div className={style.actions}>
					<ButtonGroup>
						<Button
							appearance="primary"
							onClick={() => {
								speechRecognitionStart();
							}}
						>
							{isListening ? "Stop Listening" : "Start Listening"}
						</Button>
						<Button
							disabled={speechResult?.trim()?.split(/\s+/)?.length < 60}
							appearance="primary"
							loading={isLoading}
							onClick={submitAnswer}
						>
							Submit
						</Button>
					</ButtonGroup>
				</div>
			</div>
			<FeedbackComponent
				userId={user?.userId || ""}
				speakingActivityId={activityId}
			/>
		</div>
	);
};
